<template>
  <app-modal
    id="usb-error"
    name="usb-token-error-modal"
    width="800px"
    :title="title"
    hide-footer
    @before-open="beforeOpen"
    @before-close="beforeClose"
  >
    <div class="mt-2">
      <div v-if="isLicenseExpired">
        <license-expired />
      </div>
      <div v-else-if="errorMsg" class="usb-token-error-modal__body">
        <p class="usb-token-error-modal__msg usb-token-error-modal__msg--primary">
          {{ errorMsg }}
        </p>
        <p v-if="errorDetail" class="usb-token-error-modal__msg usb-token-error-modal__msg--detail">
          <span class="usb-token-error-modal__detail-label">{{ detailLabel }}:</span>
          {{ errorDetail }}
        </p>
      </div>
      <div v-else class="usb-token-error-modal__guide">
        <p>
          1. {{ $t('releaseInvoice.modal.usb.guideStep1') }}
          <a style="text-decoration: underline" :href="downloadUrl" target="_blank">
            {{ $t('releaseInvoice.modal.usb.guideStep1Link') }}
          </a>
        </p>
        <p>2. {{ $t('releaseInvoice.modal.usb.guideStep2') }}</p>
        <p>3. {{ $t('releaseInvoice.modal.usb.guideStep3') }}</p>
        <p class="mt-3">
          <a target="_blank" style="text-decoration: underline" href="https://pvssolution.com/lien-he/">
            {{ $t('releaseInvoice.modal.usb.hsmLink') }}
          </a>
        </p>
      </div>
    </div>
    <div class="text-right">
      <button
        v-if="primaryButtonKey"
        class="btn btn-sm"
        :class="primaryButtonClass"
        @click="onPrimaryClick"
      >
        {{ $t(primaryButtonKey) }}
      </button>
    </div>
  </app-modal>
</template>
<script>

import AppModal from '@/components/app-modal/';
import { ERROR_CODES } from '@/app-constant';
import LicenseExpired from './license-expired.vue';

const BUTTON = {
  CLOSE: 'releaseInvoice.modal.buttons.close',
  RETRY_SIGN: 'releaseInvoice.modal.buttons.retrySign',
  TRY_AGAIN: 'releaseInvoice.modal.buttons.tryAgain',
  SIGN_INVOICE: 'releaseInvoice.modal.buttons.signInvoice',
};

const TITLE_KEY = {
  [ERROR_CODES.RELEASE_INVOICE_FAILED]: 'releaseInvoice.modal.titles.releaseFailed',
  [ERROR_CODES.SIGN_INVOICE_FAILED]: 'releaseInvoice.modal.titles.signFailed',
  [ERROR_CODES.CREATE_TMP_INVOICE_FAILED]: 'releaseInvoice.modal.titles.createFailed',
  [ERROR_CODES.TRANSFER_INVOICE_FAILED]: 'releaseInvoice.modal.titles.transferFailed',
  [ERROR_CODES.REPORT_INVOICE_ERROR_FAILED]: 'releaseInvoice.modal.titles.taxReportFailed',
};

const ACTION_BY_UI_CODE = {
  [ERROR_CODES.RELEASE_INVOICE_FAILED]: { button: BUTTON.CLOSE, action: 'close', primary: false },
  [ERROR_CODES.CREATE_TMP_INVOICE_FAILED]: { button: BUTTON.CLOSE, action: 'close', primary: false },
  [ERROR_CODES.REPORT_INVOICE_ERROR_FAILED]: { button: BUTTON.CLOSE, action: 'close', primary: false },
  [ERROR_CODES.TRANSFER_INVOICE_FAILED]: { button: BUTTON.TRY_AGAIN, action: 'close', primary: false },
  [ERROR_CODES.LICENSE_EXPIRED]: { button: BUTTON.CLOSE, action: 'close', primary: false },
};

const USB_CONTENT_KEY = {
  [ERROR_CODES.APP_NOT_START]: 'releaseInvoice.modal.usb.appNotStart',
  [ERROR_CODES.APP_NOT_UP_TO_DATE]: 'releaseInvoice.modal.usb.appNotUpToDate',
  [ERROR_CODES.USB_NOT_CONNECT]: 'releaseInvoice.modal.usb.usbNotConnect',
  [ERROR_CODES.TAX_CODE_ERROR]: 'releaseInvoice.modal.usb.taxCodeError',
  [ERROR_CODES.USB_NOT_ACCESSIBLE]: 'releaseInvoice.modal.usb.usbNotAccessible',
};

const USB_SETUP_CODES = new Set([
  ERROR_CODES.APP_NOT_START,
  ERROR_CODES.APP_NOT_UP_TO_DATE,
  ERROR_CODES.USB_NOT_CONNECT,
]);

export default {
  name: 'UsbTokenErrorModal',
  components: {
    LicenseExpired,
    AppModal,
  },
  data() {
    return {
      downloadUrl:
        'https://www.dropbox.com/scl/fi/9gjzdnn6hhygz8d5h2uaw/Setup1.msi?rlkey=wef77tlrhd9ahhwal40rkm831&st=41g0jcan&dl=1',
      title: '',
      errorMsg: null,
      errorDetail: null,
      isLicenseExpired: false,
      primaryButtonKey: null,
      primaryButtonClass: 'btn-outline-primary',
      primaryAction: 'close',
      errorCode: null,
      flag: null,
    };
  },
  computed: {
    detailLabel() {
      return this.$t('releaseInvoice.modal.detailLabel');
    },
  },
  methods: {
    resetState() {
      this.errorCode = null;
      this.errorMsg = null;
      this.errorDetail = null;
      this.isLicenseExpired = false;
      this.primaryButtonKey = null;
      this.primaryAction = 'close';
      this.primaryButtonClass = 'btn-outline-primary';
    },
    beforeClose() {
      this.resetState();
    },
    setPrimaryButton({ button, action, primary }) {
      this.primaryButtonKey = button;
      this.primaryAction = action;
      this.primaryButtonClass = primary ? 'btn-primary' : 'btn-outline-primary';
    },
    applyUiCodeAction(uiCode) {
      if (uiCode === ERROR_CODES.SIGN_INVOICE_FAILED) {
        const canRetry = this.flag === 'CREATE' || this.flag === 'ACTIONS';
        this.setPrimaryButton({
          button: canRetry ? BUTTON.RETRY_SIGN : BUTTON.CLOSE,
          action: 'close',
          primary: canRetry,
        });
        return;
      }
      const cfg = ACTION_BY_UI_CODE[uiCode] || {
        button: BUTTON.CLOSE,
        action: 'close',
        primary: false,
      };
      this.setPrimaryButton(cfg);
    },
    applyUsbCode(code) {
      const canRetrySign = this.flag === 'CREATE' || this.flag === 'ACTIONS';
      const isSetupIssue = USB_SETUP_CODES.has(code);

      this.title = this.$t(
        isSetupIssue
          ? 'releaseInvoice.modal.titles.usbSetup'
          : 'releaseInvoice.modal.titles.usbError'
      );

      if (isSetupIssue) {
        this.errorMsg = null;
        if (canRetrySign) {
          this.setPrimaryButton({
            button: BUTTON.SIGN_INVOICE,
            action: 'sign_bus',
            primary: true,
          });
        } else {
          this.setPrimaryButton({
            button: BUTTON.CLOSE,
            action: 'close',
            primary: false,
          });
        }
        return;
      }

      this.errorMsg = this.$t(USB_CONTENT_KEY[code]);
      this.setPrimaryButton({
        button: BUTTON.CLOSE,
        action: 'close',
        primary: false,
      });
    },
    applyStructuredError(uiCode, payload) {
      const titleKey = TITLE_KEY[uiCode];
      this.title = titleKey
        ? this.$t(titleKey)
        : this.$t('releaseInvoice.modal.titles.generic');

      const fallbackKey = `releaseInvoice.modal.fallback.${uiCode}`;
      const fallback = this.$t(fallbackKey);
      const fallbackText = fallback !== fallbackKey ? fallback : '';

      this.errorMsg = String(payload.message || '').trim() || fallbackText || '';
      const extra = payload.detail ? String(payload.detail).trim() : '';
      this.errorDetail = extra && extra !== this.errorMsg ? extra : null;
      this.applyUiCodeAction(uiCode);
    },
    applyFieldValidationError(fieldKey, messages) {
      this.title = this.$t('releaseInvoice.modal.titles.validationFailed');
      const label = this.fieldLabel(fieldKey);
      const msg = Array.isArray(messages) ? messages[0] : messages;
      this.errorMsg = label && msg ? `${label}: ${msg}` : (msg || label);
      this.errorDetail = null;
      this.applyUiCodeAction(ERROR_CODES.CREATE_TMP_INVOICE_FAILED);
    },
    fieldLabel(fieldKey) {
      const labelKey = `actions.form.${fieldKey}.label`;
      const label = this.$t(labelKey);
      return label && label !== labelKey ? label : fieldKey;
    },
    beforeOpen(params) {
      this.resetState();
      this.flag = params.model.flag || null;
      this.errorCode = params.model.error;

      if (typeof this.errorCode === 'object') {
        if (this.errorCode.message_code && this.errorCode.message) {
          this.applyStructuredError(this.errorCode.message_code, this.errorCode);
          return;
        }
        const entries = Object.entries(this.errorCode);
        if (entries.length) {
          const [fieldKey, messages] = entries[0];
          this.applyFieldValidationError(fieldKey, messages);
        }
        return;
      }

      if (this.errorCode === ERROR_CODES.LICENSE_EXPIRED) {
        this.title = this.$t('releaseInvoice.modal.titles.licenseExceeded');
        this.isLicenseExpired = true;
        this.applyUiCodeAction(ERROR_CODES.LICENSE_EXPIRED);
        return;
      }

      if (USB_CONTENT_KEY[this.errorCode]) {
        this.applyUsbCode(this.errorCode);
        return;
      }

      if (TITLE_KEY[this.errorCode]) {
        this.title = this.$t(TITLE_KEY[this.errorCode]);
        const fallbackKey = `releaseInvoice.modal.fallback.${this.errorCode}`;
        const fallback = this.$t(fallbackKey);
        this.errorMsg = fallback !== fallbackKey ? fallback : '';
        this.applyUiCodeAction(this.errorCode);
        return;
      }

      this.title = this.$t('releaseInvoice.modal.titles.generic');
      this.errorMsg = this.$t('releaseInvoice.modal.fallback.RELEASE_INVOICE_FAILED');
      this.applyUiCodeAction(ERROR_CODES.RELEASE_INVOICE_FAILED);
    },
    onPrimaryClick() {
      this.$modal.hide('usb-token-error-modal');
      if (this.primaryAction !== 'sign_bus') {
        return;
      }
      setTimeout(() => {
        if (this.flag === 'CREATE') {
          this.$bus.emit('create-invoice');
        } else if (this.flag === 'ACTIONS') {
          this.$bus.emit('actions-invoice');
        }
      }, 500);
    },
  },
};
</script>
<style lang="scss">
#usb-error {
  font-size: 14px;

  p {
    margin-bottom: .5rem;
  }

  .usb-token-error-modal__msg {
    white-space: pre-line;
  }

  .usb-token-error-modal__msg--primary {
    font-weight: 500;
    margin-bottom: 0.75rem;
  }

  .usb-token-error-modal__msg--detail {
    color: #555;
    font-size: 13px;
    margin-top: 0;
  }

  .usb-token-error-modal__detail-label {
    font-weight: 600;
  }

  .modal-header {
    color: #FC5555;
    background: #CCE4F6;
    border-radius: 5px;
  }

  .close {
    color: #FC5555;
    opacity: 1;
  }
}
</style>



// WEBPACK FOOTER //
// src/portal-components/invoice/usb-token-error-modal.vue